import * as React from "react";
import styled, { css } from "styled-components";
import { v4 as uuidV4 } from "uuid";

import {
  breakpoint,
  Colors
} from "src/library/utils";
import { Tooltip } from "src/library/components/Tooltip";

import { LoadingSpinner } from "../LoadingSpinner";
import { Icon } from "../Icon";
import { Icons } from "../Icon/types";
import { Text } from "../Text";

export type ButtonType = "primary" | "secondary" | "submit" | "error" | "plain" | "smart";

export type CondenseType = "micro" | "condensed" | "small" | "medium" | "large";

export interface IButtonProps {
  className?: string;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactChild;
  background?: keyof Colors;
  color?: keyof Colors;
  borderColor?: keyof Colors;
  hoverStyles?: React.CSSProperties;
  $buttonType?: ButtonType;
  size?: CondenseType;
  type?: "submit" | "reset" | "button";
  icon?: Icons;
  iconSize?: number;
  iconPosition?: "left" | "right";
  newTabPath?: string;
  tooltip?: string;
}

interface IButtonStyleProps extends Pick<IButtonProps, "disabled" | "background" | "color" | "borderColor" | "hoverStyles" | "$buttonType" | "size" | "icon" | "iconPosition"> {
  $loading?: boolean;
  hasNewTabOption?: boolean;
}

const PrimaryButtonStyles = css`
  border: 2px ${({ theme }) => theme.colors.primaryLight} solid;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.primaryLight};

  .icon {
    fill: ${({ theme }) => theme.colors.white}!important;
  }
`;

const SmartButtonStyles = css`
  color: ${({ theme }) => theme.colors.white};
  background: transparent linear-gradient(102deg, #FBB03B 0%, #BA3CB5 100%) 0% 0% no-repeat padding-box;
  position: relative;
  
  .icon {
    fill: ${({ theme }) => theme.colors.white}!important;
  }
`;

const SecondaryButtonStyles = css`
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.primaryLight};
  border: 2px solid ${({ theme }) => theme.colors.primaryLight};

  &:hover {
    color: ${({ theme }) => theme.colors.white};
    background: ${({ theme }) => theme.colors.primaryLight};

    svg {
      fill: ${({ theme }) => theme.colors.white};
    }
  }
`;

const ErrorButtonStyles = css`
  border: none;
  background: ${({ theme }) => theme.colors.error};
`;

const PlainButtonStyles = css`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.primaryLight};

  .icon {
    fill: ${({ theme }) => theme.colors.primaryLight};
  }
`;

const getStylesByType = (type?: ButtonType) => {
  switch (type) {
    case "primary":
    case "submit":
      return PrimaryButtonStyles;

    case "secondary":
      return SecondaryButtonStyles;

    case "error":
      return ErrorButtonStyles;

    case "plain":
      return PlainButtonStyles;

    case "smart":
      return SmartButtonStyles;

    default:
      return PrimaryButtonStyles;
  }
};

export const ButtonStyles = css<IButtonStyleProps>`
  ${({
    theme,
    background,
    color,
    borderColor,
    hoverStyles,
    size,
    disabled,
    $loading,
    $buttonType,
    hasNewTabOption
  }) => css`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  min-height: 35px;
  line-height: 35px;
  outline: none;
  cursor: pointer;
  text-align: center;

  ${getStylesByType($buttonType)};

  ${size ? sizeCss(size) : ""}
  font-family: ${theme.fonts.family.body || "Roboto"};
  font-size: ${theme.fonts.sizes.p};
  background: ${background ? theme.colors[ background ] : ""};
  font-weight: ${theme.fonts.weights.normal};
  ${!!color && `color:  ${theme.colors[ color ]};`}
  ${!!borderColor && `border:  2px solid ${theme.colors[ borderColor ]};`}
  border-radius: ${theme.borderRadius};
  border-top-right-radius: ${hasNewTabOption ? 0 : theme.borderRadius};
  border-bottom-right-radius: ${hasNewTabOption ? 0 : theme.borderRadius};
  padding: 0 ${theme.spacing.padding.md};
  transition: ${theme.transition.quicker};
  cursor: pointer;

  &:hover {
    transition: ${theme.transition.quicker};
    ${hoverStyles ? { ...hoverStyles } : "opacity: 0.8"};
  }

  ${breakpoint("sm", theme)}{
    font-size: ${theme.fonts.sizes.p};
  }

  .icon-left {
    margin-right: ${theme.spacing.margins.sm};
  }

  .icon-right {
    margin-left: ${theme.spacing.margins.sm};
  }

  ${$loading &&
    css`
      opacity: 0.8;
      cursor: wait;
    `}

    ${disabled &&
      css`
        cursor: not-allowed;
        background: ${theme.colors.ghost};
        border-color: ${theme.colors.ghost};
      `}

  `}
`;

const sizeCss = (size: CondenseType) => {
  switch (size) {
    default:
    case "condensed":
      return sizeCondensedCss;

    case "micro":
      return sizeMicroCss;

    case "small":
      return sizeSmallCss;

    case "medium":
      return sizeMediumCss;

    case "large":
      return sizeLargeCss;
  }
};

const sizeCondensedCss = css`
  min-width: 0;
`;

const sizeMicroCss = css`
  min-width: 0;
  min-height: 26px;
  line-height: 26px;
`;

const sizeSmallCss = css`
  min-width: 84px;
`;

const sizeMediumCss = css`
  min-width: 140px;
`;

const sizeLargeCss = css`
  min-width: 194px;
`;

const ButtonStyled = styled("button")<IButtonStyleProps>`
  ${ButtonStyles}
`;

const ButtonWrapper = styled.div<IButtonStyleProps>`
  ${({ theme }) => css`
    position: relative;
    display: flex;
    flex-direction: row;
    
    .new-tab {
      ${ButtonStyles};
      border-top-left-radius: 0;
      border-bottom-left-radius: 0; 
      border-top-right-radius: ${theme.borderRadius}; 
      border-bottom-right-radius: ${theme.borderRadius}; 
      border-left: none;
    }
  `}
`;

const Button: React.ReactFCWithChildren<React.ButtonHTMLAttributes<HTMLButtonElement> & IButtonProps> = ({
  $buttonType,
  loading,
  disabled,
  children,
  type,
  size,
  icon,
  iconSize,
  iconPosition,
  newTabPath,
  className,
  onClick,
  tooltip,
  ...rest
}) => {
  const $buttonTypeDefault = "primary";
  const sizeDefault = "condensed";
  const [ tooltipId, setTooltipId ] = React.useState<string | null>(null);

  // if a tooltip message is set then setup a unique id for this button for the tippy id
  React.useEffect(() => {
    if (tooltip && !tooltipId) {
      setTooltipId(uuidV4());
    }
  }, [ tooltip, tooltipId ]);

  return (
    <ButtonWrapper
      hasNewTabOption={!!newTabPath}
      $buttonType={$buttonType || $buttonTypeDefault}
      size={size || sizeDefault}
      disabled={disabled || loading}
      $loading={loading}
      className="button-wrapper"
      data-tip
      data-for={tooltipId || ""}
    >
      {tooltipId && (
        <Tooltip
          id={tooltipId}
          type="dark"
          place="top"
          getContent={() => {
            return (
              <Text color="white">
                <>
                  {tooltip}
                </>
              </Text>
            );
          }}
        />
      )}

      <ButtonStyled
        {...rest}
        className={`button ${className}`}
        hasNewTabOption={!!newTabPath}
        onClick={onClick}
        type={type ? type : "button"}
        $buttonType={$buttonType || $buttonTypeDefault}
        size={size || sizeDefault}
        disabled={disabled || loading}
        $loading={loading}
      >
        <>
          {!!icon && (iconPosition === "left" || !iconPosition) && (
            <Icon
              className={`icon icon-${iconPosition || "left"}`}
              icon={icon}
              size={iconSize ? iconSize : 15}
            />
          )}

          {loading ? (
            <LoadingSpinner
              type="circular"
              position="inline"
              size={iconSize ? iconSize : 15}
              color={$buttonType === "secondary" ? "primaryLight" : "white"}
            />
          ) : (
            children
          )}

          {!!icon && iconPosition === "right" && (
            <Icon
              icon={icon}
              className={`icon icon-${iconPosition}`}
              size={iconSize ? iconSize : 15}
            />
          )}
        </>
      </ButtonStyled>

      {newTabPath && (
        <div
          className="new-tab"
          onClick={() => window.open(newTabPath, "_blank")}
          title="Open in new tab"
        >
          <Icon
            size={iconSize ? iconSize : 16}
            icon={Icons.OPENNEW}
            className="icon"
            $hoverColor={$buttonType === "primary" ? undefined : "white"}
          />
        </div>
      )}
    </ButtonWrapper>
  );
};

export default Button;
